/// InstanceList: Displays process instances with state indicators.
/// Active=green, Completed=gray, Failed=red, Suspended=yellow.
/// Updates in real-time via engineStore reactive subscriptions.

import { Play, Check, X, AlertTriangle, Clock } from "lucide-react";
import type { ProcessInstance } from "@/types/engine";
import type { InstanceState } from "@/types/engine";

// ---------------------------------------------------------------------------
// State configuration
// ---------------------------------------------------------------------------

interface StateConfig {
  dotColor: string;
  textColor: string;
  icon: typeof Play;
  label: string;
}

const STATE_CONFIGS: Record<InstanceState, StateConfig> = {
  created: {
    dotColor: "bg-blue-400",
    textColor: "text-blue-400",
    icon: Clock,
    label: "Created",
  },
  running: {
    dotColor: "bg-green-400",
    textColor: "text-green-400",
    icon: Play,
    label: "Active",
  },
  completed: {
    dotColor: "bg-zinc-400",
    textColor: "text-zinc-400",
    icon: Check,
    label: "Completed",
  },
  failed: {
    dotColor: "bg-red-400",
    textColor: "text-red-400",
    icon: X,
    label: "Failed",
  },
  suspended: {
    dotColor: "bg-yellow-400",
    textColor: "text-yellow-400",
    icon: AlertTriangle,
    label: "Suspended",
  },
};

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

interface InstanceListProps {
  instances: ProcessInstance[];
}

/**
 * List of process instances with state indicators.
 * Shown when expanding a process in the ProcessList.
 */
export function InstanceList({ instances }: InstanceListProps) {
  if (instances.length === 0) {
    return (
      <div className="ml-6 border-l border-zinc-700 pl-3 py-1">
        <span className="text-xs text-zinc-600 italic">No instances</span>
      </div>
    );
  }

  return (
    <div className="ml-6 border-l border-zinc-700">
      {instances.map((instance) => (
        <InstanceItem key={instance.instanceKey} instance={instance} />
      ))}
    </div>
  );
}

function InstanceItem({ instance }: { instance: ProcessInstance }) {
  const config = STATE_CONFIGS[instance.state] ?? STATE_CONFIGS.running;
  const shortKey =
    instance.instanceKey.length > 8
      ? instance.instanceKey.slice(0, 8) + "..."
      : instance.instanceKey;

  const elapsed = instance.completedAt
    ? formatDuration(instance.completedAt - instance.startedAt)
    : formatDuration(Date.now() - instance.startedAt);

  return (
    <div className="flex items-center gap-2 px-3 py-1 text-xs hover:bg-zinc-800 rounded group cursor-default">
      {/* State indicator dot */}
      <div
        className={[
          "h-2 w-2 rounded-full flex-shrink-0",
          config.dotColor,
          instance.state === "running" ? "animate-pulse" : "",
        ].join(" ")}
      />

      {/* Instance key */}
      <span className="flex-1 truncate text-zinc-300 font-mono" title={instance.instanceKey}>
        {shortKey}
      </span>

      {/* Current element (if running) */}
      {instance.currentElement && instance.state === "running" && (
        <span className="text-zinc-500 truncate max-w-[80px]" title={instance.currentElement}>
          {instance.currentElement}
        </span>
      )}

      {/* Duration */}
      <span
        className="text-zinc-500 tabular-nums"
        title={instance.state === "running" ? "Elapsed" : "Duration"}
      >
        {elapsed}
      </span>

      {/* State label */}
      <span className={["text-xs flex-shrink-0", config.textColor].join(" ")}>{config.label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}
